<template>
  <View direction="column" class="thread">
    <template v-for="item in threadItems" :key="item.key">
      <View v-if="item.type === 'day'" align="center" role="separator" class="day-divider">
        <Text variant="label-small" as="span" color="tertiary" class="day-label">{{ item.label }}</Text>
      </View>
      <ConversationChatMessage
        v-else-if="item.type === 'message'"
        :message="item.message"
        :time-zone="conversation.propertyTimeZone"
      />
      <ConversationEventRow v-else :copy="item.copy" :created-at="item.createdAt" />
    </template>
  </View>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { Text, View } from '@tallo/design-system';

import { ConversationDto } from '../models/conversation.interface';
import { MessageDto } from '../models/message.interface';
import { TraceEventCopy, buildTraceEventCopy } from '../traces/trace-event-copy.function';
import ConversationChatMessage from './conversation-chat-message.vue';
import ConversationEventRow from './conversation-event-row.vue';

type ThreadItem =
  | { type: 'day'; key: string; label: string }
  | { type: 'message'; key: string; createdAt: string; message: MessageDto }
  | { type: 'event'; key: string; createdAt: string; copy: TraceEventCopy };

const props = defineProps<{ conversation: ConversationDto }>();

const renterFirstName = computed(() => {
  const renterMessage = props.conversation.messages.find((message) => !message.isSystem && message.user?.name);
  return renterMessage?.user?.name.split(' ')[0] || 'The renter';
});

const threadItems = computed<ThreadItem[]>(() => {
  const entries: Exclude<ThreadItem, { type: 'day' }>[] = props.conversation.messages.map((message) => ({
    type: 'message',
    key: `message-${message.id}`,
    createdAt: message.createdAt,
    message,
  }));

  (props.conversation.events ?? []).forEach((trace) => {
    // Copy resolves to null for traces that must stay invisible (e.g. a silent turn's strategies);
    // filtering here keeps day dividers from pointing at empty days.
    const copy = buildTraceEventCopy(trace, renterFirstName.value, props.conversation.propertyTimeZone);
    if (copy) {
      entries.push({ type: 'event', key: `event-${trace.id}`, createdAt: trace.createdAt, copy });
    }
  });

  entries.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const items: ThreadItem[] = [];
  let lastDay = '';
  entries.forEach((entry) => {
    const day = new Date(entry.createdAt).toDateString();
    if (day !== lastDay) {
      items.push({
        type: 'day',
        key: `day-${day}`,
        label: new Date(entry.createdAt).toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        }),
      });
      lastDay = day;
    }
    items.push(entry);
  });

  return items;
});
</script>

<style scoped lang="scss">
.thread {
  position: relative;
  width: 100%;

  // The rail: one vertical line through all entry nodes — an activity ledger, not a bubble chat.
  &::before {
    content: '';
    position: absolute;
    left: 0.8125rem;
    top: 1rem;
    bottom: 0.75rem;
    width: 1px;
    background: var(--t-color-border-secondary);
  }
}

// A run of consecutive events is one beat, so it keeps the tighter message rhythm inside the run
// and spends the extra air only where the run meets a message or a divider.
.event-row + .event-row {
  padding-top: 0.1875rem;
}

.day-divider {
  position: relative;
  z-index: 1;
  // Sits between the sm and md spacing tokens: tight enough that the label and its rule read as
  // one divider, wide enough that the rule does not touch the text.
  gap: 0.625rem;
  padding: 1.125rem 0 0.625rem;

  // The thread opens on a divider; its top pad would just be dead space above the first entry.
  &:first-child {
    padding-top: 0;
  }

  .day-label {
    // A notch below label-small: this is the quietest text in the thread, a date stamp rather
    // than a label on anything.
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    // Opaque so the rail does not show through where the label overlaps it.
    background: var(--t-color-neutral-white);
    padding-right: var(--t-spacing-xs);
  }

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--t-color-border-secondary);
  }
}
</style>
